import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Memory, FlightTakeoff } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { fleetRefreshRequested } from '../../store/slices/dronesSlice';
import { EnrolDroneDialog } from './EnrolDroneDialog';
import { VirtualDroneDialog } from './VirtualDroneDialog';
import { tutorialTarget } from '../../tutorial/tours';

/**
 * One way to register a drone (#389, closing #525).
 *
 * There were two controls. One sat top-left inside the map's control column and
 * enrolled a real airframe; the other was a floating action button bottom-right
 * and created a simulated one. They were reported from the screen as "one looks
 * like legacy while the other looks like a new one" — neither was. They were two
 * different things, and nothing on the screen said which was which.
 *
 * So there is one control now, and it asks the question that actually matters
 * before anything else happens: real hardware, or simulation? The distinction is
 * the most important one on this screen, and it is the one that used to be
 * invisible.
 *
 * Each branch keeps its own dialog unchanged. Physical drones have a
 * shown-once-credentials flow with its own rules about not being dismissed
 * (#470); virtual drones have a list, a per-operator cap and a simulator that can
 * be absent from the cluster (#322, #526). Merging those into one form would make
 * both worse.
 */

/** Which kind of aircraft the pilot is adding, or null while they choose. */
type RegisterKind = 'physical' | 'virtual' | null;

interface KindChoice {
  kind: Exclude<RegisterKind, null>;
  title: string;
  /** What this choice does, stated before it is taken. */
  detail: string;
  icon: React.ReactNode;
}

const CHOICES: KindChoice[] = [
  {
    kind: 'physical',
    title: 'Physical drone',
    detail:
      'A real airframe with its own companion computer. Issues credentials to install on the aircraft, shown once.',
    icon: <Memory />,
  },
  {
    kind: 'virtual',
    title: 'Virtual drone',
    detail:
      'A simulated aircraft the backend runs as a pod. No hardware; use it to fly a mission and see the fleet work.',
    icon: <FlightTakeoff />,
  },
];

/**
 * The chooser. Rendered as its own dialog rather than a step inside one of the
 * others, so neither branch has to know the other exists.
 */
const ChooseKindDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onChoose: (kind: Exclude<RegisterKind, null>) => void;
}> = ({ open, onClose, onChoose }) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
    <DialogTitle>Register a drone</DialogTitle>
    <DialogContent>
      <Stack spacing={1.5} mt={1}>
        {CHOICES.map((choice) => (
          <Button
            key={choice.kind}
            variant="outlined"
            onClick={() => onChoose(choice.kind)}
            startIcon={choice.icon}
            // Left-aligned with the sentence underneath, because the sentence is
            // the point: a centred label alone is what the two old buttons had.
            sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 1.5 }}
          >
            <Box>
              <Typography component="span" display="block" fontWeight={600}>
                {choice.title}
              </Typography>
              <Typography
                component="span"
                display="block"
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'none' }}
              >
                {choice.detail}
              </Typography>
            </Box>
          </Button>
        ))}
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
    </DialogActions>
  </Dialog>
);

/** The fleet view's single entry point for adding an aircraft of either kind. */
export const RegisterDroneButton: React.FC<{ onRegistered?: () => void }> = ({
  onRegistered,
}) => {
  const [choosing, setChoosing] = useState(false);
  const [kind, setKind] = useState<RegisterKind>(null);
  const dispatch = useDispatch();

  // Refresh the fleet here rather than leaving it to the caller. The fleet is
  // fetched once on mount, so without this the drone the pilot just created is
  // absent from the list they created it from — which reads as registration
  // having failed (#480).
  const handleRegistered = () => {
    dispatch(fleetRefreshRequested());
    onRegistered?.();
  };

  const choose = (chosen: Exclude<RegisterKind, null>) => {
    setChoosing(false);
    setKind(chosen);
  };

  return (
    <>
      {/* The accessible name stays short and distinct; the sentence that
          explains the choice belongs in the dialog, where there is room for one
          per kind. A screen reader announcing "add" tells a pilot no more than a
          centred icon tells a sighted one (#525). */}
      <Tooltip title="Register a drone — real hardware or a simulated aircraft">
        <Fab
          color="primary"
          size="small"
          aria-label="Register a drone"
          onClick={() => setChoosing(true)}
          {...tutorialTarget('fleet-register-drone')}
        >
          <Add />
        </Fab>
      </Tooltip>

      <ChooseKindDialog
        open={choosing}
        onClose={() => setChoosing(false)}
        onChoose={choose}
      />

      <EnrolDroneDialog
        open={kind === 'physical'}
        onClose={() => setKind(null)}
        onEnrolled={handleRegistered}
      />
      <VirtualDroneDialog open={kind === 'virtual'} onClose={() => setKind(null)} />
    </>
  );
};
